/**
 * Search snippets arrive as `ts_headline` output: plain text with delimiters
 * around the matched terms. Rather than trusting it as HTML, the snippet is
 * split on the known delimiters and rebuilt out of text nodes plus <mark>
 * elements — nothing else in the string can become markup.
 */

import { el, fragment } from "./dom";

/** Matches the delimiters Postgres is configured to emit (and common variants). */
const DELIMITER = /<\/?(?:mark|b|strong|em)>|\[\[hl\]\]|\[\[\/hl\]\]/gi;

function isOpening(token: string): boolean {
  const lower = token.toLowerCase();
  return lower === "[[hl]]" || (lower.startsWith("<") && !lower.startsWith("</"));
}

export function renderSnippet(snippet: string): DocumentFragment {
  if (snippet === "") {
    return fragment();
  }

  const parts: Node[] = [];
  let cursor = 0;
  let highlighting = false;

  DELIMITER.lastIndex = 0;
  for (let match = DELIMITER.exec(snippet); match !== null; match = DELIMITER.exec(snippet)) {
    const chunk = snippet.slice(cursor, match.index);
    if (chunk !== "") {
      parts.push(highlighting ? el("mark", { text: chunk }) : document.createTextNode(chunk));
    }
    highlighting = isOpening(match[0]);
    cursor = match.index + match[0].length;
  }

  const tail = snippet.slice(cursor);
  if (tail !== "") {
    parts.push(highlighting ? el("mark", { text: tail }) : document.createTextNode(tail));
  }

  return fragment(...parts);
}

/** Highlights every occurrence of `needle` in `haystack`, for client-side filters. */
export function highlightSubstring(haystack: string, needle: string): DocumentFragment {
  const trimmed = needle.trim();
  if (trimmed === "") {
    return fragment(haystack);
  }

  const parts: Node[] = [];
  const lowerHaystack = haystack.toLowerCase();
  const lowerNeedle = trimmed.toLowerCase();
  let cursor = 0;

  for (;;) {
    const index = lowerHaystack.indexOf(lowerNeedle, cursor);
    if (index === -1) {
      break;
    }
    if (index > cursor) {
      parts.push(document.createTextNode(haystack.slice(cursor, index)));
    }
    parts.push(el("mark", { text: haystack.slice(index, index + lowerNeedle.length) }));
    cursor = index + lowerNeedle.length;
  }

  if (cursor < haystack.length) {
    parts.push(document.createTextNode(haystack.slice(cursor)));
  }

  return fragment(...parts);
}
